import { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowLeft, Check, Loader2, ShieldCheck, Clock, Phone } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { DuckBadge } from "@/components/DuckMascot";
import { submitLead } from "@/lib/api";
import { PESTS, getPest } from "@/data/pests";
import { URGENCY_OPTIONS } from "@/data/site";
import { getCity, getLandingPage, AFTERCARE } from "@/data/locations";
import { trackConversion } from "@/lib/track";

/**
 * Paid-traffic landing funnel. Deliberately different from /hire-a-pro:
 * no header, no footer, no outbound links — every link on a page you're
 * paying for clicks to is a leak.
 *
 * Step order is urgency → contact → address. That looks like more typing than
 * a short form, but every text field carries a real `autocomplete` token and
 * the whole thing is a single <form>, so a phone fills name, phone, email and
 * the full address in one tap. Autofill inverts the usual "fewer fields wins"
 * rule: a long autofilled form beats a short hand-typed one.
 *
 * The street address matters because it's what makes the lead sellable — a
 * contractor quoting a property needs to know which property.
 */

const SLUG_TO_OPTION = {
  mosquitoes: "Mosquitoes", ticks: "Ticks", "wasps-hornets": "Wasps/Hornets",
  ants: "Ants", fleas: "Fleas", rodents: "Rodents",
};

const TRUST = [
  { icon: ShieldCheck, text: "No obligation" },
  { icon: Clock, text: "Takes about a minute" },
  { icon: Phone, text: "Local CT providers" },
];

function Progress({ step, total }) {
  return (
    <div className="flex gap-1.5" aria-hidden>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
            i <= step ? "bg-duckgreen" : "bg-cream/20"
          }`}
        />
      ))}
    </div>
  );
}

// Big tappable card. Selecting auto-advances — no extra "Next" press per step.
function Choice({ label, icon: Icon, accent, onSelect, selected }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full flex items-center gap-4 p-4 sm:p-5 rounded-2xl border-2 text-left transition-all active:scale-[0.98] ${
        selected ? "border-duckgreen bg-duckgreen/10" : "border-forest/12 bg-cream hover:border-forest/30"
      }`}
      data-testid={`funnel-choice-${label}`}
    >
      {Icon && (
        <span
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${accent}1A` }}
        >
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </span>
      )}
      <span className="font-semibold text-forest-950 text-[17px] leading-snug">{label}</span>
    </button>
  );
}

const FIELD =
  "w-full px-5 py-4 rounded-2xl bg-cream text-forest-950 text-base focus:outline-none focus:ring-4 focus:ring-duckgreen/40";
const LABEL = "block text-sm font-medium text-cream/70 mb-1.5";

export default function Funnel() {
  const { pestSlug, citySlug } = useParams();
  const [params] = useSearchParams();
  const preset = pestSlug ? getPest(pestSlug) : null;
  const city = citySlug ? getCity(citySlug) : null;
  // Only combinations declared in data/locations.js exist. A URL that isn't on
  // that list is a real 404, not a rendered page — no soft-404 farm.
  const local = pestSlug && citySlug ? getLandingPage(pestSlug, citySlug) : null;
  const badCity = Boolean(citySlug) && (!city || !local);

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    pest: preset ? SLUG_TO_OPTION[preset.slug] : "",
    urgency: "",
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    street: "",
    // Prefilled from the landing page when we know the town — one less thing
    // to type, and still editable for anyone just over the line.
    city: city?.name || "",
    state: city?.state || "CT",
    zip_code: "",
  });

  // A funnel opened at /lp/ticks skips the pest question — the ad already
  // established it, so re-asking wastes the click.
  const steps = useMemo(
    () => (preset ? ["urgency", "contact", "address"] : ["pest", "urgency", "contact", "address"]),
    [preset]
  );
  const current = steps[step];

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  // Placed after every hook on purpose: an early return above them would make
  // hook order conditional and break the Rules of Hooks.
  if (badCity) return <Navigate to="/lp" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const advance = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));
  const pick = (k, v) => { set(k, v); setTimeout(advance, 180); };

  const contactValid =
    form.first_name.trim() && form.last_name.trim() && /^[\d\s().+-]{10,}$/.test(form.phone);
  const addressValid = form.street.trim().length > 3 && /^\d{5}$/.test(form.zip_code);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!addressValid) return toast.error("Add the street address and ZIP so a pro can quote the property.");
    setBusy(true);
    try {
      await submitLead({
        ...form,
        property_type: "",
        preferred_contact: "Phone call",
        description: "",
        consent: true,
        // /lp/* exists only for paid traffic. Honour a real UTM if the ad
        // platform sent one; otherwise mark it paid so it never reads organic.
        utm_medium: params.get("utm_medium") || "paid",
      });
      // Tell the ad platforms a click became a lead. Without this, Smart
      // Bidding optimises for clicks and spends budget on traffic that
      // never converts.
      trackConversion({
        pest: form.pest,
        city: form.city || city?.name || null,
        zip_code: form.zip_code,
        urgency: form.urgency,
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium") || "paid",
        utm_campaign: params.get("utm_campaign"),
      });
      setDone(true);
    } catch {
      toast.error("Something went wrong. Try again?");
    } finally {
      setBusy(false);
    }
  };

  const heading = local
    ? local.headline
    : preset
    ? `${preset.name} in your yard? Get a Connecticut pro on it.`
    : "Get a Connecticut pest pro on it.";

  if (done) {
    const care = AFTERCARE[pestSlug] || null;
    return (
      <div className="min-h-screen bg-forest-950 grain px-4 py-12">
        <Seo title="Request received" description="We've got your request." path="/lp" noindex />
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md mx-auto"
          data-testid="funnel-done"
        >
          <div className="rounded-3xl bg-cream p-8 sm:p-10 text-center">
            <div className="flex justify-center mb-5">
              <div className="relative">
                <DuckBadge size={76} />
                <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-duckgreen flex items-center justify-center ring-4 ring-cream">
                  <Check className="w-5 h-5 text-forest-950" />
                </div>
              </div>
            </div>
            <h1 className="font-serif text-3xl font-bold text-forest-950">You're all set, {form.first_name}.</h1>
            <p className="mt-3 text-forest-950/70 leading-relaxed">
              We've got your {form.pest?.toLowerCase() || "pest"} request for {form.street}
              {form.city ? `, ${form.city}` : ""}. A local provider will be in touch shortly — most reach out the same day.
            </p>
          </div>

          {/* Post-conversion, so it costs nothing and is genuinely useful.
              Free measures that work, which is the brand's whole position. */}
          {care && (
            <section className="mt-6 rounded-3xl border border-cream/12 p-6 sm:p-7" data-testid="funnel-aftercare">
              <h2 className="font-serif text-xl font-bold text-cream leading-snug">{care.title}</h2>
              <ul className="mt-4 space-y-3">
                {care.items.map((it) => (
                  <li key={it} className="flex gap-3 text-sm text-cream/75 leading-relaxed">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-duckgreen shrink-0" />
                    {it}
                  </li>
                ))}
              </ul>
              {care.note && <p className="mt-4 text-xs text-cream/45 leading-relaxed">{care.note}</p>}
            </section>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-forest-950 grain flex flex-col">
      <Seo
        title={local ? local.headline : preset ? `${preset.name} Control — Connecticut` : "Pest Control — Connecticut"}
        description="Tell us what's bugging you and we'll help connect you with a local Connecticut pest-control provider."
        path={local ? `/lp/${local.pest}/${local.city}` : preset ? `/lp/${preset.slug}` : "/lp"}
        noindex
      />

      {/* Deliberately not a nav — the badge is not a link. */}
      <header className="px-4 sm:px-6 pt-6 pb-4 max-w-lg w-full mx-auto">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2.5">
            <DuckBadge size={34} />
            <span className="font-serif text-lg font-bold text-cream">DuckTheBugs</span>
          </div>
          {step > 0 && (
            <button type="button" onClick={back} className="flex items-center gap-1 text-sm text-cream/60 hover:text-cream px-2 py-2 -mr-2" data-testid="funnel-back">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}
        </div>
        <Progress step={step} total={steps.length} />
      </header>

      <main className="flex-1 px-4 sm:px-6 pb-10 max-w-lg w-full mx-auto">
        <h1 className="mt-4 font-serif text-3xl sm:text-4xl font-bold text-cream leading-[1.1]">{heading}</h1>
        {local?.sub && (
          <p className="mt-3 text-[15px] text-cream/70 leading-relaxed">{local.sub}</p>
        )}

        {/* One real <form>: browsers autofill far more reliably inside one,
            and it gives Enter-to-submit for free. */}
        <form onSubmit={submit} noValidate>
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.2 }}
              className="mt-7"
            >
              {current === "pest" && (
                <>
                  <p className="text-cream/70 mb-4">What's the problem?</p>
                  <div className="space-y-2.5">
                    {PESTS.map((p) => (
                      <Choice
                        key={p.slug}
                        label={p.name}
                        icon={Icons[p.icon]}
                        accent={p.accent}
                        selected={form.pest === SLUG_TO_OPTION[p.slug]}
                        onSelect={() => pick("pest", SLUG_TO_OPTION[p.slug])}
                      />
                    ))}
                  </div>
                </>
              )}

              {current === "urgency" && (
                <>
                  <p className="text-cream/70 mb-4">How soon do you need help?</p>
                  <div className="space-y-2.5">
                    {URGENCY_OPTIONS.map((u) => (
                      <Choice key={u} label={u} selected={form.urgency === u} onSelect={() => pick("urgency", u)} />
                    ))}
                  </div>
                </>
              )}

              {current === "contact" && (
                <>
                  <p className="text-cream/70 mb-4">Who should the provider ask for?</p>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={LABEL} htmlFor="fn">First name</label>
                        <input id="fn" name="first_name" autoComplete="given-name" autoFocus
                          value={form.first_name} onChange={(e) => set("first_name", e.target.value)}
                          className={FIELD} data-testid="funnel-first-name" />
                      </div>
                      <div>
                        <label className={LABEL} htmlFor="ln">Last name</label>
                        <input id="ln" name="last_name" autoComplete="family-name"
                          value={form.last_name} onChange={(e) => set("last_name", e.target.value)}
                          className={FIELD} data-testid="funnel-last-name" />
                      </div>
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="ph">Phone</label>
                      <input id="ph" name="phone" type="tel" inputMode="tel" autoComplete="tel"
                        value={form.phone} onChange={(e) => set("phone", e.target.value)}
                        className={FIELD} data-testid="funnel-phone" />
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="em">Email <span className="text-cream/40 font-normal">(optional)</span></label>
                      <input id="em" name="email" type="email" inputMode="email" autoComplete="email"
                        value={form.email} onChange={(e) => set("email", e.target.value)}
                        className={FIELD} data-testid="funnel-email" />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => (contactValid ? advance() : toast.error("Add your name and a phone number."))}
                    disabled={!contactValid}
                    className="mt-4 w-full py-4 rounded-2xl bg-duckgreen text-forest-950 font-bold text-lg disabled:opacity-35 active:scale-[0.98] transition-all"
                    data-testid="funnel-contact-next"
                  >
                    Continue
                  </button>
                </>
              )}

              {current === "address" && (
                <>
                  <p className="text-cream/70 mb-4">Where's the property?</p>
                  <div className="space-y-3">
                    <div>
                      <label className={LABEL} htmlFor="st">Street address</label>
                      <input id="st" name="street" autoComplete="address-line1" autoFocus
                        value={form.street} onChange={(e) => set("street", e.target.value)}
                        className={FIELD} data-testid="funnel-street" />
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-3">
                      <div>
                        <label className={LABEL} htmlFor="ct">Town</label>
                        <input id="ct" name="city" autoComplete="address-level2"
                          value={form.city} onChange={(e) => set("city", e.target.value)}
                          className={FIELD} data-testid="funnel-city" />
                      </div>
                      <div className="w-24">
                        <label className={LABEL} htmlFor="stt">State</label>
                        <input id="stt" name="state" autoComplete="address-level1"
                          value={form.state} onChange={(e) => set("state", e.target.value)}
                          className={FIELD} data-testid="funnel-state" />
                      </div>
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="zp">ZIP code</label>
                      <input id="zp" name="zip_code" type="tel" inputMode="numeric" maxLength={5}
                        autoComplete="postal-code"
                        value={form.zip_code} onChange={(e) => set("zip_code", e.target.value.replace(/\D/g, ""))}
                        className={FIELD} data-testid="funnel-zip" />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={busy || !addressValid}
                    className="mt-4 w-full py-4 rounded-2xl bg-duckgreen text-forest-950 font-bold text-lg disabled:opacity-35 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                    data-testid="funnel-submit"
                  >
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                    {busy ? "Sending…" : "Get my match"}
                  </button>
                  <p className="mt-3 text-xs text-cream/50 leading-relaxed text-center">
                    By submitting you agree to be contacted about your request. DuckTheBugs is an independent
                    matching service and does not perform pesticide applications.
                  </p>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </form>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          {TRUST.map((t) => (
            <span key={t.text} className="flex items-center gap-1.5 text-xs text-cream/50">
              <t.icon className="w-3.5 h-3.5" /> {t.text}
            </span>
          ))}
        </div>

        {/* Local proof, deliberately BELOW the form. A paid click should reach
            the first question with no scrolling; this is for whoever scrolls.
            Still no outbound links except the state data source. */}
        {local && city && (
          <section className="mt-12 pt-10 border-t border-cream/10" data-testid="funnel-local">
            <img
              src={city.image}
              alt={city.imageAlt}
              loading="lazy"
              width="1200"
              height="1200"
              className="w-full max-w-xs mx-auto rounded-2xl"
            />
            <h2 className="mt-7 font-serif text-2xl font-bold text-cream leading-snug">
              Why {city.name} homeowners deal with this
            </h2>
            <ul className="mt-4 space-y-3.5">
              {local.facts.map((f) => (
                <li key={f} className="flex gap-3 text-sm text-cream/75 leading-relaxed">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-duckgreen shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            {local.source && (
              <p className="mt-5 text-xs text-cream/45 leading-relaxed">
                Source:{" "}
                <a
                  href={local.source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-cream/70 transition-colors"
                >
                  {local.source.label}
                </a>
              </p>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
